export const PLUGIN_CATEGORIES: Record<number, string> = {
  0: 'Client',
  1: 'Model Provider',
  2: 'Development & Platform',
  3: 'Blockchain & Web3',
};

export const PLUGIN_NAMES = [
  // 0 - Client
  '@elizaos/plugin-discord',
  '@elizaos/plugin-farcaster',
  '@elizaos/plugin-telegram',
  '@elizaos/plugin-twitter',

  // 1 - Model Provider
  '@elizaos/plugin-anthropic',
  '@elizaos/plugin-groq',
  '@elizaos/plugin-local-ai',
  '@elizaos/plugin-openai',
  '@elizaos/plugin-venice',

  // 2 - Development & Platform
  '@fleek-platform/eliza-plugin-mcp',
  '@elizaos/plugin-browser',
  '@elizaos/plugin-pdf',
  '@elizaos/plugin-redpill',
  '@elizaos/plugin-sql',
  '@elizaos/plugin-storage-s3',
  '@elizaos/plugin-video-understanding',

  // 3 - Blockchain & Web3
  '@elizaos/plugin-evm',
  '@elizaos/plugin-solana',
] as const;

export type Plugin = (typeof PLUGIN_NAMES)[number];
type CategoryLabelDescription = {
  category: number;
  label: string;
  description: string;
};

export const PLUGINS_MAP: Record<Plugin, CategoryLabelDescription> = {
  // 0 - Client
  '@elizaos/plugin-discord': {
    category: 0,
    label: 'Discord',
    description:
      'A Discord client implementation for ElizaOS, enabling rich integration with Discord servers for managing interactions, voice, and message handling.',
  },
  '@elizaos/plugin-farcaster': {
    category: 0,
    label: 'Farcaster',
    description:
      'A plugin for ElizaOS that enables agent integration with the Farcaster social network.',
  },
  '@elizaos/plugin-telegram': {
    category: 0,
    label: 'Telegram',
    description:
      'This plugin integrates a Telegram client with ElizaOS, allowing characters in ElizaOS to interact via Telegram.',
  },
  '@elizaos/plugin-twitter': {
    category: 0,
    label: 'Twitter',
    description:
      'This plugin integrates a Twitter client with ElizaOS, allowing characters in ElizaOS to interact via Twitter.',
  },

  // 1 - Model Provider
  '@elizaos/plugin-anthropic': {
    category: 1,
    label: 'Anthropic Claude',
    description:
      "This plugin provides integration with Anthropic's Claude models through the ElizaOS platform.",
  },
  '@elizaos/plugin-groq': {
    category: 1,
    label: 'Groq',
    description: 'A Groq plugin for ElizaOS.',
  },
  '@elizaos/plugin-local-ai': {
    category: 1,
    label: 'Local AI',
    description:
      'This plugin provides local AI model capabilities through the ElizaOS platform, supporting text generation, image analysis, speech synthesis, and audio transcription.',
  },
  '@elizaos/plugin-openai': {
    category: 1,
    label: 'OpenAI',
    description:
      "This plugin provides integration with OpenAI's models through the ElizaOS platform.",
  },
  '@elizaos/plugin-redpill': {
    category: 1,
    label: 'RedPill',
    description:
      "This plugin provides integration with RedPill's model aggregator through the ElizaOS platform.",
  },
  '@elizaos/plugin-venice': {
    category: 1,
    label: 'Venice',
    description:
      'Venice AI plugin for ElizaOS. This plugin provides integration with the Venice AI API for text generation and object generation capabilities.',
  },

  // 2 - Development & Platform
  '@fleek-platform/eliza-plugin-mcp': {
    category: 2,
    label: 'MCP Plugin',
    description:
      'This plugin integrates the Model Context Protocol (MCP) with ElizaOS, allowing agents to connect to multiple MCP servers and use their resources, prompts, and tools.',
  },
  '@elizaos/plugin-browser': {
    category: 2,
    label: 'Node.js',
    description:
      'Core Node.js plugin for Eliza OS that provides essential services and actions for file operations, media processing, and cloud integrations.',
  },
  '@elizaos/plugin-pdf': {
    category: 0,
    label: 'PDF',
    description:
      'Core Node.js plugin for Eliza OS that provides essential services and actions for file operations, media processing, and cloud integrations.',
  },
  '@elizaos/plugin-sql': {
    category: 0,
    label: 'Drizzle Database Adapter',
    description:
      'A PostgreSQL database adapter built with Drizzle ORM for the ElizaOS ecosystem.',
  },
  '@elizaos/plugin-storage-s3': {
    category: 0,
    label: 'AWS S3',
    description:
      'Provides AWS S3 integration for cloud-based file operations and storage management.',
  },
  '@elizaos/plugin-video-understanding': {
    category: 0,
    label: 'Video understanding',
    description:
      'Core Node.js plugin for Eliza OS that provides essential services and actions for file operations, media processing, and cloud integrations.',
  },

  // 3 - Blockchain & Web3
  '@elizaos/plugin-evm': {
    category: 3,
    label: 'EVM',
    description:
      'This plugin provides actions and providers for interacting with EVM-compatible chains.',
  },
  '@elizaos/plugin-solana': {
    category: 3,
    label: 'Solana',
    description:
      'Core Solana blockchain plugin for Eliza OS that provides essential services and actions for token operations, trading, and DeFi integrations.',
  },
};

export const SECRETS_PLUGIN_MAP: Record<Plugin, Record<string, string>> = {
  '@elizaos/plugin-anthropic': {
    ANTHROPIC_API_KEY: '',
  },
  '@elizaos/plugin-browser': {},
  '@elizaos/plugin-discord': {
    DISCORD_APPLICATION_ID: '',
    DISCORD_API_TOKEN: '',
  },
  '@elizaos/plugin-evm': {
    EVM_PRIVATE_KEY: '',
  },
  '@elizaos/plugin-farcaster': {
    FARCASTER_NEYNAR_API_KEY: '',
    FARCASTER_NEYNAR_SIGNER_UUID: '',
    FARCASTER_FID: '',
  },
  '@elizaos/plugin-groq': {
    GROQ_API_KEY: '',
  },
  '@elizaos/plugin-local-ai': {
    USE_LOCAL_AI: 'true',
    USE_STUDIOLM_TEXT_MODELS: 'false',
    STUDIOLM_SERVER_URL: 'http://localhost:1234',
    STUDIOLM_SMALL_MODEL: 'lmstudio-community/deepseek-r1-distill-qwen-1.5b',
    STUDIOLM_MEDIUM_MODEL: 'deepseek-r1-distill-qwen-7b',
    STUDIOLM_EMBEDDING_MODEL: 'false',
  },
  '@elizaos/plugin-openai': {
    OPENAI_API_KEY: '',
  },
  '@elizaos/plugin-pdf': {},
  '@elizaos/plugin-redpill': {
    REDPILL_API_KEY: '',
  },
  '@elizaos/plugin-solana': {
    WALLET_SECRET_KEY: '',
    WALLET_PUBLIC_KEY: '',
    SOL_ADDRESS: '',
    SLIPPAGE: '',
    SOLANA_RPC_URL: '',
    HELIUS_API_KEY: '',
    BIRDEYE_API_KEY: '',
  },
  '@elizaos/plugin-sql': {},
  '@elizaos/plugin-storage-s3': {
    AWS_ACCESS_KEY_ID: '',
    AWS_SECRET_ACCESS_KEY: '',
    AWS_REGION: '',
    AWS_S3_BUCKET: '',
    AWS_S3_UPLOAD_PATH: '',
    AWS_S3_ENDPOINT: '',
    AWS_S3_SSL_ENABLED: '',
    AWS_S3_FORCE_PATH_STYLE: '',
  },
  '@elizaos/plugin-telegram': {
    TELEGRAM_BOT_TOKEN: '',
  },
  '@elizaos/plugin-twitter': {
    TWITTER_USERNAME: '',
    TWITTER_PASSWORD: '',
    TWITTER_EMAIL: '',
    TWITTER_2FA_SECRET: '',
    TWITTER_ENABLE_POST_GENERATION: 'true',
    POST_IMMEDIATELY: 'true',
  },
  '@elizaos/plugin-venice': {
    VENICE_API_KEY: '',
  },
  '@elizaos/plugin-video-understanding': {},
  '@fleek-platform/eliza-plugin-mcp': {},
};
